import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Motion";
import { PRICING } from "@/lib/pricing";
import { BRAND } from "@/lib/brand";
import { Home, CalendarClock, Cog, Cpu, LayoutTemplate, Phone, ArrowRight } from "lucide-react";

const SECTIONS = [
  { key: "onetime", icon: Home, num: "01" },
  { key: "maintenance", icon: CalendarClock, num: "02" },
  { key: "equipment", icon: Cog, num: "03" },
  { key: "neptune", icon: Cpu, num: "04" },
  { key: "newtank", icon: LayoutTemplate, num: "05" },
];

function Table({ data }) {
  const cols = data.headers ? data.headers.length : 2;
  return (
    <div className="rounded-2xl border border-white/10 overflow-hidden">
      {data.headers && (
        <div className={`grid ${cols === 3 ? "grid-cols-[1fr_auto_auto] gap-x-6" : "grid-cols-[1fr_auto]"} px-5 py-3 bg-white/[0.04] border-b border-white/10`}>
          {data.headers.map((h) => <span key={h} className="mono-label text-white/45">{h}</span>)}
        </div>
      )}
      {data.rows.map((r, i) => (
        <div key={i} className={`grid ${cols === 3 ? "grid-cols-[1fr_auto_auto] gap-x-6" : "grid-cols-[1fr_auto]"} items-center px-5 py-3.5 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors`}>
          <span className="text-white/80 text-sm md:text-base">{r[0]}</span>
          {r.slice(1).map((c, j) => (
            <span key={j} className={`font-mono text-sm md:text-base text-right ${j === r.length - 2 ? "text-cyan" : "text-white/50"}`}>{c}</span>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function Pricing() {
  return (
    <div className="max-w-[1100px] mx-auto px-5 md:px-10 pt-36 pb-28 min-h-screen">
      <Reveal>
        <span className="mono-label text-cyan">Transparent pricing</span>
        <h1 className="font-display text-5xl md:text-7xl tracking-tight mt-4 mb-4">Service rate card</h1>
        <p className="text-white/55 max-w-2xl">Clear, upfront pricing for maintenance, installations and Neptune automation across South Florida. Every job is confirmed with a quote before we start — ranges depend on tank size, access and scope.</p>
      </Reveal>

      <div className="mt-16 space-y-16">
        {SECTIONS.map((s, i) => {
          const data = PRICING[s.key];
          return (
            <motion.section key={s.key}
              initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 2) * 0.05 }}>
              <div className="flex items-start gap-5 mb-6">
                <div className="font-mono text-4xl md:text-5xl text-white/10 leading-none pt-1">{s.num}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <s.icon className="text-cyan" size={22} />
                    <h2 className="font-display text-3xl md:text-4xl">{data.title}</h2>
                  </div>
                  <p className="text-white/45 text-sm">{data.note}</p>
                </div>
              </div>
              <Table data={data} />
            </motion.section>
          );
        })}
      </div>

      {/* CTA */}
      <Reveal>
        <div className="mt-20 glass rounded-3xl p-10 md:p-14 border border-cyan/20 text-center" style={{ boxShadow: "0 20px 80px -30px rgba(0,240,255,0.3)" }}>
          <h2 className="font-display text-4xl md:text-5xl mb-4">Ready to book?</h2>
          <p className="text-white/55 max-w-xl mx-auto mb-8">Reserve your service date online with a deposit, or reach out for a custom quote on larger builds and monthly plans.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/services" data-testid="pricing-book-btn" className="btn-pill btn-solid flex items-center gap-2">Book a service <ArrowRight size={15} /></Link>
            <a href={BRAND.phoneHref} className="btn-pill btn-pink flex items-center gap-2"><Phone size={15} /> Call {BRAND.phone}</a>
          </div>
          <p className="text-white/30 text-xs mt-6 font-mono">Prices are estimates and may vary by tank size, access and scope. Custom quotes available on request.</p>
        </div>
      </Reveal>
    </div>
  );
}
